import * as React from 'react';
import {useEffect, useState} from 'react';
import {ConfirmationFormModal} from '../../shared/confirmation-form/ConfirmationFormModal';
import {ConfirmationFormRowString} from '../../shared/confirmation-form/ConfirmationFormRowString';
import ConfirmationFormRow from '../../shared/confirmation-form/ConfirmationFormRow';
import {ResponseError} from '../../shared/CommonInterfaces';
import {ConfirmationFormFooter} from '../../shared/confirmation-form/ConfirmationFormFooter';
import {ConfirmationFormError} from '../../shared/confirmation-form/ConfirmationFormError';
import {renderLocationLink} from '../../../libs/helpers';
import {isNullOrEmpty} from '../../shared/StringHelper';
import {LoadStatusMap} from './LoadConstants';
import {UpsertLoadState} from './LoadInterfaces';

export interface Props {
  loadStatus: string;
  sortCode?: string;
  loadData: UpsertLoadState; // load id, scac, trailer #, dock location
  customerLoadIdRequired: boolean;

  onEditLoad: (upsert: UpsertLoadState) => Promise<ResponseError>;
  onConfirm: () => Promise<ResponseError>;
  toggle: () => void;
}

// Similar to LoadShipConfirmation modal, with fields specific to sortation parcel load
export const SortationParcelLoadShipConfirmation: React.FC<Props> = (props) => {
  useEffect(() => {
    checkShipEligibility();
  }, [props.loadData]);

  const [enableShip, setEnableShip] = useState<boolean>(false);
  const [error, setError] = useState<ResponseError>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const statusObject = LoadStatusMap.get(props.loadStatus);
  const statusClassName = `shipment-status-dot ${statusObject?.className}`;
  const footerTextWOCustomerLoadId = '*Carrier and Trailer # are required to ship this load.';
  const footerTextWithCustomerLoadId = '*Carrier, Trailer # and Customer Load Id are required to ship this load.';

  /**
   * Data edited in this modal is saved by the parent.
   * Pass T/F around so everyone knows when a failure occurs so that the value remains in sync across components.
   *
   * For each editable field, we check that the data actually changed before sending the edit to the server
   * We can assume that nulls will be passed in place of empty strings.
   */

  const onSaveScac = async (newScac: string): Promise<boolean> => {
    if (newScac === props.loadData.scac) {
      return true;
    }
    const upsert = {...props.loadData, scac: newScac};
    return await upsertLoad(upsert);
  };

  const onSaveTrailerNumber = async (newTrailerNumber: string): Promise<boolean> => {
    if (newTrailerNumber === props.loadData.trailerNumber) {
      return true;
    }
    const upsert = {...props.loadData, trailerNumber: newTrailerNumber};
    return await upsertLoad(upsert);
  };

  const onSaveCustomerLoadId = async (newCustomerLoadId: string): Promise<boolean> => {
    if (newCustomerLoadId === props.loadData.customerLoadId) {
      return true;
    }
    const upsert = {...props.loadData, customerLoadId: newCustomerLoadId};
    return await upsertLoad(upsert);
  };

  const upsertLoad = async (upsert: UpsertLoadState): Promise<boolean> => {
    setError(null);
    const upsertError = await props.onEditLoad(upsert);

    if (upsertError) {
      setError(upsertError);
      return false;
    } else {
      checkShipEligibility();
      return true;
    }
  };

  const onConfirm = async () => {
    setError(null);
    setSubmitting(true);
    setEnableShip(false);
    const confirmError = await props.onConfirm();
    if (confirmError) {
      setError(confirmError);
    }
    setEnableShip(true);
    setSubmitting(false);
  };

  const checkShipEligibility = () => {
    if (!props.loadData) {
      setEnableShip(false);
      return;
    }

    const requiredLoadData = props.loadData;
    const isValidString = (str: string): boolean => str != null && str.length > 0;
    const isValidNumber = (num: number): boolean => num != null && num > 0;

    // these requirements must remain in sync with ConfirmationFormRows rendered with the 'requiresAction' class name
    const requirements: boolean[] = [];
    requirements.push(isValidString(requiredLoadData.scac));
    requirements.push(isValidString(requiredLoadData.trailerNumber));
    requirements.push(isValidNumber(requiredLoadData.dockLocationId));
    if (props.customerLoadIdRequired) {
      requirements.push(isValidString(requiredLoadData.customerLoadId));
    }
    setEnableShip(requirements.every((req) => req === true));
  };

  return (
    <React.Fragment>
      <ConfirmationFormModal title={`Complete Load ${props.loadData?.loadId}`} toggle={props.toggle}>
        <ConfirmationFormRow name={'Status'} requiresAction={false}>
          <div>
            <span className={statusClassName} />
            {statusObject?.text}
          </div>
        </ConfirmationFormRow>
        <ConfirmationFormRow name={'Location'} requiresAction={props.loadData?.dockLocationId === null}>
          <div>
            <span className={'link'}>
              {renderLocationLink(props.loadData?.dockLocationId, props.loadData?.dockLocationLabel)}
            </span>
          </div>
        </ConfirmationFormRow>
        <ConfirmationFormRow name={'Carrier (SCAC)'} requiresAction={isNullOrEmpty(props.loadData?.scac)}>
          <ConfirmationFormRowString
            value={props.loadData?.scac}
            placeholderValue={'Add SCAC'}
            isEditable={true}
            onSave={onSaveScac}
          />
        </ConfirmationFormRow>
        <ConfirmationFormRow name={'Trailer #'} requiresAction={isNullOrEmpty(props.loadData?.trailerNumber)}>
          <ConfirmationFormRowString
            value={props.loadData?.trailerNumber}
            placeholderValue={'Add Trailer #'}
            isEditable={true}
            onSave={onSaveTrailerNumber}
          />
        </ConfirmationFormRow>
        <ConfirmationFormRow name={'Sort Code'} requiresAction={false}>
          <ConfirmationFormRowString value={props.sortCode} placeholderValue={'--'} isEditable={false} />
        </ConfirmationFormRow>
        {props.customerLoadIdRequired && (
          <ConfirmationFormRow name={'Customer Load Id'} requiresAction={isNullOrEmpty(props.loadData?.customerLoadId)}>
            <ConfirmationFormRowString
              value={props.loadData?.customerLoadId}
              placeholderValue={'Add Customer Load Id'}
              isEditable={true}
              onSave={onSaveCustomerLoadId}
            />
          </ConfirmationFormRow>
        )}
        <ConfirmationFormError error={error} />
        <ConfirmationFormFooter
          buttonText={'Ship Load'}
          footerText={props.customerLoadIdRequired ? footerTextWithCustomerLoadId : footerTextWOCustomerLoadId}
          enableButton={enableShip}
          onConfirm={onConfirm}
          submitting={submitting}
        />
      </ConfirmationFormModal>
    </React.Fragment>
  );
};
